import React, { useMemo, useState } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Code } from 'lucide-react';
import { formatProperty, isBiogenicSentinel } from '../utils/okobaudatProperties';

const PROPERTY_ORDER = [
  'bulkDensity',
  'grossDensity',
  'surfaceWeight',
  'layerThickness',
  'linearDensity',
  'conversionFactor',
  'biogenicCarbonContent'
];

const PROPERTY_LABELS = {
  bulkDensity: 'Bulk density',
  grossDensity: 'Gross density',
  surfaceWeight: 'Grammage',
  layerThickness: 'Layer thickness',
  linearDensity: 'Linear density',
  conversionFactor: 'Conversion factor',
  biogenicCarbonContent: 'Biogenic carbon content'
};

const DENSITY_KEYS = ['grossDensity', 'bulkDensity', 'density'];
const LAYER_THICKNESS_KEYS = ['layerThickness'];
const GRAMMAGE_KEYS = ['surfaceWeight'];

function approximatelyEqual(a, b, epsilon = 1e-6) {
  if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
  return Math.abs(a - b) <= epsilon * Math.max(1, Math.abs(a), Math.abs(b));
}

function normalizeUnitString(rawUnit) {
  if (!rawUnit) {
    return null;
  }
  return String(rawUnit)
    .trim()
    .replace(/m\^?3|m3/gi, 'm³')
    .replace(/m\^?2|m2/gi, 'm²');
}

function toArray(value) {
  if (value == null) return [];
  return Array.isArray(value) ? value : [value];
}

function extractUnitFromCandidate(candidate) {
  if (!candidate) return null;
  if (typeof candidate === 'string') {
    return normalizeUnitString(candidate);
  }
  if (Array.isArray(candidate)) {
    for (const item of candidate) {
      const resolved = extractUnitFromCandidate(item);
      if (resolved) return resolved;
    }
    return null;
  }
  if (typeof candidate === 'object') {
    const nestedKeys = [
      'unit',
      'unitName',
      'referenceUnit',
      'name',
      'shortName',
      'Name',
      'code',
      'description',
      'value',
      'text'
    ];
    for (const key of nestedKeys) {
      if (candidate[key]) {
        const resolved = extractUnitFromCandidate(candidate[key]);
        if (resolved) return resolved;
      }
    }
  }
  return null;
}

function resolveRawUnit(property) {
  if (!property?.raw || typeof property.raw !== 'object') {
    return null;
  }

  const raw = property.raw;
  const candidates = [
    raw.unit,
    raw.unitName,
    raw.referenceUnit,
    raw.propertyData?.Unit,
    raw.propertyData?.unit,
    raw.propertyData?.Units,
    raw.propertyData?.ReferenceUnit,
    raw.propertyData?.referenceUnit,
    raw.metadata?.Units?.Unit,
    raw.metadata?.Units,
    raw.metadata?.Unit,
    raw.metadata?.units,
    raw.metadata?.ReferenceUnit,
    raw.metadata?.referenceUnit
  ]
    .flatMap((candidate) => toArray(candidate));

  for (const candidate of candidates) {
    const resolved = extractUnitFromCandidate(candidate);
    if (resolved) {
      return resolved;
    }
  }

  return null;
}

function resolveMetadataLabel(property) {
  const metadataName = property?.raw?.metadata?.Name;
  if (!metadataName) return null;
  if (typeof metadataName === 'string') {
    const trimmed = metadataName.trim();
    return trimmed.length ? trimmed : null;
  }
  if (Array.isArray(metadataName)) {
    for (const entry of metadataName) {
      const resolved = resolveMetadataLabel({ raw: { metadata: { Name: entry } } });
      if (resolved) return resolved;
    }
    return null;
  }
  if (typeof metadataName === 'object') {
    const candidates = [metadataName.value, metadataName.text, metadataName.name];
    for (const candidate of candidates) {
      if (typeof candidate === 'string') {
        const trimmed = candidate.trim();
        if (trimmed.length) return trimmed;
      }
    }
  }
  return null;
}

function findPropertyByKeys(properties, keys) {
  for (const key of keys) {
    const match = properties.find((property) => property.key === key);
    if (match) {
      return match;
    }
  }
  return null;
}

function findPropertyByLabel(properties, terms) {
  const lowered = terms.map((term) => term.toLowerCase());
  return properties.find((property) => {
    const labelSource = (resolveMetadataLabel(property) || property.label || property.key || '').toLowerCase();
    return lowered.some((term) => labelSource.includes(term));
  });
}

function parseDeclaredUnitInfo(declaredUnit, extractedDeclaredUnit) {
  const amountFromDeclared = Number.isFinite(Number(declaredUnit?.amount)) ? Number(declaredUnit.amount) : null;
  const unitFromDeclared = normalizeUnitString(declaredUnit?.unit);

  if (!extractedDeclaredUnit && amountFromDeclared != null && unitFromDeclared) {
    return { amount: amountFromDeclared, unit: unitFromDeclared };
  }

  const raw = typeof extractedDeclaredUnit === 'string' ? extractedDeclaredUnit.trim() : '';
  if (!raw) {
    return { amount: amountFromDeclared, unit: unitFromDeclared };
  }

  const match = raw.match(/^[^0-9+-]*([+-]?\d+(?:[.,]\d+)?)/);
  const amount = match ? Number(match[1].replace(',', '.')) : amountFromDeclared;

  const unitPart = raw.replace(match ? match[0] : '', '').trim();
  const normalizedUnit = unitPart ? normalizeUnitString(unitPart) : unitFromDeclared;

  return {
    amount: Number.isFinite(amount) ? amount : amountFromDeclared,
    unit: normalizedUnit || unitFromDeclared
  };
}

function shouldDisplayProperty(property, declaredUnit, extractedDeclaredUnit) {
  if (!property) return false;
  if (property.key === 'surfaceWeight') {
    return true;
  }

  const normalizedKey = (property.key || '').toLowerCase();
  const label = (resolveMetadataLabel(property) || property.label || property.key || '').toLowerCase();
  if (label.includes('weight') && property.key !== 'surfaceWeight') {
    return false;
  }

  const propertyValue = Number.isFinite(Number(property.value)) ? Number(property.value) : null;
  const propertyUnit = normalizeUnitString(property.unit);

  const { amount: declaredAmount, unit: declaredUnitNormalized } = parseDeclaredUnitInfo(
    declaredUnit,
    extractedDeclaredUnit
  );

  const matchesDeclaredAmount =
    declaredAmount != null && propertyValue != null && approximatelyEqual(propertyValue, declaredAmount);
  const matchesDeclaredUnit =
    declaredUnitNormalized && propertyUnit ? propertyUnit === declaredUnitNormalized : !propertyUnit;
  const looksLikeArea =
    label.includes('area') ||
    label.includes('surface') ||
    normalizedKey.includes('area') ||
    normalizedKey.includes('surface');
  const looksLikeDeclaredUnit =
    normalizedKey.includes('declared_unit') || label.includes('declared unit') || normalizedKey.includes('declaredunit');
  const looksLikeVolume = normalizedKey.includes('volume') || label.includes('volume');

  if (looksLikeDeclaredUnit) {
    return false;
  }

  if (looksLikeArea && matchesDeclaredAmount && matchesDeclaredUnit) {
    return false;
  }

  if (looksLikeVolume && matchesDeclaredAmount) {
    return false;
  }

  if (matchesDeclaredAmount && matchesDeclaredUnit) {
    const duplicateHints = ['reference amount', 'reference flow', 'reference quantity', 'declared', 'decl'];
    if (
      duplicateHints.some((hint) => {
        const normalizedHint = hint.replace(/\s+/g, '').toLowerCase();
        const underscoredHint = hint.replace(/\s+/g, '_').toLowerCase();
        return (
          label.includes(hint) ||
          normalizedKey.includes(hint) ||
          normalizedKey.includes(underscoredHint) ||
          normalizedKey.includes(normalizedHint)
        );
      })
    ) {
      return false;
    }
  }

  return true;
}

function normalizePropertyUnit(property) {
  if (!property) return property;
  const normalizedUnit = normalizeUnitString(property.unit);
  if (!normalizedUnit) {
    return property;
  }
  if (normalizedUnit === property.unit) {
    return property;
  }
  return { ...property, unit: normalizedUnit };
}

function applyFallbackUnit(property) {
  if (!property) return property;
  const preferred = normalizeUnitString(property.unit);
  const lowerKey = (property.key || '').toLowerCase();
  const label = (resolveMetadataLabel(property) || property.label || '').toLowerCase();

  const hasMeaningfulUnit = Boolean(preferred && (/[\/⁻]/.test(preferred) || /m²|m³|m⁻²|m⁻³/.test(preferred)));

  if (!hasMeaningfulUnit) {
    if (lowerKey.includes('density') || label.includes('density')) {
      return { ...property, unit: 'kg/m³' };
    }
    if (
      lowerKey.includes('surfaceweight') ||
      lowerKey.includes('surface_weight') ||
      lowerKey.includes('grammage') ||
      label.includes('grammage')
    ) {
      return { ...property, unit: 'kg/m²' };
    }
  }

  if (preferred && preferred !== property.unit) {
    return { ...property, unit: preferred };
  }

  return property;
}

function ensureUnit(property) {
  if (!property) return property;
  const normalizedCurrent = normalizeUnitString(property.unit);
  const inferred = resolveRawUnit(property);

  if (property.unit) {
    if (inferred) {
      const hasInformativeUnit = Boolean(
        normalizedCurrent && (/[\/⁻]/.test(normalizedCurrent) || /m²|m³/.test(normalizedCurrent))
      );
      if (!hasInformativeUnit || normalizedCurrent !== inferred) {
        return { ...property, unit: inferred };
      }
    }
    if (normalizedCurrent && normalizedCurrent !== property.unit) {
      return { ...property, unit: normalizedCurrent };
    }
    return property;
  }

  if (inferred) {
    return { ...property, unit: inferred };
  }

  if (normalizedCurrent) {
    return { ...property, unit: normalizedCurrent };
  }

  return property;
}

function formatConversionFactor(property) {
  if (!property || property.value == null) {
    return '—';
  }

  return formatProperty(property);
}

function resolveDeclaredUnitLabel(declaredUnit, extractedDeclaredUnit) {
  if (declaredUnit?.unit) {
    const numericAmount = Number.isFinite(Number(declaredUnit.amount)) ? Number(declaredUnit.amount) : null;
    const amountLabel = numericAmount != null ? formatProperty({ value: numericAmount, unit: null }) : '1';
    return `${amountLabel} ${declaredUnit.unit}`.trim();
  }
  if (typeof extractedDeclaredUnit === 'string' && extractedDeclaredUnit.trim().length > 0) {
    return extractedDeclaredUnit;
  }
  return null;
}

function prepareProperty(property) {
  if (!property) return null;
  return applyFallbackUnit(normalizePropertyUnit(ensureUnit(property)));
}

function renderValue(property) {
  if (!property) return '—';
  if (property.key === 'biogenicCarbonContent') {
    return isBiogenicSentinel(property.value) ? 'ND' : formatProperty(property);
  }
  if (property.key === 'conversionFactor') {
    return formatConversionFactor(property);
  }
  return formatProperty(property);
}

function getDisplayLabel(property, declaredUnitLabel, extractedDeclaredUnit) {
  if (!property) return 'Property';
  if (property.key === 'surfaceWeight') {
    const metadataLabel = resolveMetadataLabel(property);
    if (metadataLabel && metadataLabel.toLowerCase().includes('grammage')) {
      return 'Grammage';
    }
    return 'Grammage';
  }

  const metadataLabel = resolveMetadataLabel(property);
  if (metadataLabel) {
    return metadataLabel;
  }

  if (property.key === 'conversionFactor' && property.raw?.metadata) {
    const metaName = property.raw.metadata?.Name;
    const metaLabel =
      (typeof metaName === 'string' && metaName.trim()) ||
      (typeof metaName?.value === 'string' && metaName.value.trim());
    if (metaLabel && metaLabel.toLowerCase() !== 'conversion factor') {
      return metaLabel;
    }
  }

  if (property.key === 'conversionFactor' && (declaredUnitLabel || extractedDeclaredUnit)) {
    const descriptor = declaredUnitLabel || extractedDeclaredUnit;
    return `Conversion factor to ${descriptor}`;
  }

  return PROPERTY_LABELS[property.key] || property.label || property.key;
}

const MaterialProperties = ({ materialProperties, declaredUnit }) => {
  const [additionalOpen, setAdditionalOpen] = useState(false);
  const [rawState, setRawState] = useState({ open: false, property: null });

  const extractedDeclaredUnit = materialProperties?.declaredUnit;

  const declaredUnitLabel = useMemo(
    () => resolveDeclaredUnitLabel(declaredUnit, extractedDeclaredUnit),
    [declaredUnit, extractedDeclaredUnit]
  );

  const preparedProperties = useMemo(() => {
    const entries = materialProperties?.properties || {};
    const keys = PROPERTY_ORDER.filter((key) => entries[key]);
    const additionalKeys = Object.keys(entries).filter((key) => !PROPERTY_ORDER.includes(key));
    const orderedKeys = [...keys, ...additionalKeys];

    return orderedKeys
      .map((key) => entries[key])
      .filter(Boolean)
      .filter((property) => shouldDisplayProperty(property, declaredUnit, extractedDeclaredUnit))
      .map((property) => prepareProperty(property))
      .filter(Boolean);
  }, [materialProperties?.properties, declaredUnit, extractedDeclaredUnit]);

  const featuredProperties = useMemo(() => {
    if (!preparedProperties.length) {
      return [];
    }

    const densityProperty =
      findPropertyByKeys(preparedProperties, DENSITY_KEYS) ||
      findPropertyByLabel(preparedProperties, ['density']);

    const layerThicknessProperty =
      findPropertyByKeys(preparedProperties, LAYER_THICKNESS_KEYS) ||
      findPropertyByLabel(preparedProperties, ['thickness']);

    const grammageProperty =
      findPropertyByKeys(preparedProperties, GRAMMAGE_KEYS) ||
      findPropertyByLabel(preparedProperties, ['grammage', 'surface weight']);

    const secondProperty = layerThicknessProperty || grammageProperty;

    const candidates = [densityProperty, secondProperty].filter(Boolean);
    const unique = [];
    for (const property of candidates) {
      if (property && !unique.includes(property)) {
        unique.push(property);
      }
    }
    return unique;
  }, [preparedProperties]);

  const secondaryProperties = useMemo(
    () => preparedProperties.filter((property) => !featuredProperties.includes(property)),
    [preparedProperties, featuredProperties]
  );

  const handleOpenRaw = (property) => {
    if (!property?.raw) return;
    setRawState({ open: true, property });
  };

  const handleCloseRaw = () => setRawState({ open: false, property: null });

  if (featuredProperties.length === 0 && secondaryProperties.length === 0) {
    return null;
  }

  return (
    <>
      {featuredProperties.map((property) => {
        const label = getDisplayLabel(property, declaredUnitLabel, extractedDeclaredUnit);
        const value = renderValue(property);
        const formattedLabel = label ? label.charAt(0).toUpperCase() + label.slice(1) : '';
        return (
          <Badge
            key={property.key}
            variant="outline"
            className="border-emerald-300 bg-white text-gray-900 flex items-center gap-1"
          >
            <span>{formattedLabel}:</span>
            <span className="font-medium text-gray-900 leading-none">{value}</span>
          </Badge>
        );
      })}

      {secondaryProperties.length > 0 && (
        <Dialog open={additionalOpen} onOpenChange={setAdditionalOpen}>
          <DialogTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center rounded-md border border-emerald-500 bg-emerald-500 px-2.5 py-0.5 text-xs font-semibold text-white transition-colors hover:bg-emerald-600 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-emerald-500 focus-visible:ring-offset-2"
            >
              Additional info
            </button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Additional material info</DialogTitle>
              <DialogDescription>
                Extended metadata supplied by the OEKOBAUDAT dataset.
              </DialogDescription>
            </DialogHeader>
            <div className="overflow-hidden rounded-md border border-emerald-100">
              <table className="w-full text-sm">
                <thead className="bg-emerald-50 text-emerald-700">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Property</th>
                    <th className="px-3 py-2 text-left font-medium">Value</th>
                    <th className="px-3 py-2 text-right font-medium">Raw</th>
                  </tr>
                </thead>
                <tbody>
                  {secondaryProperties.map((property) => {
                    const label = getDisplayLabel(property, declaredUnitLabel, extractedDeclaredUnit);
                    const value = renderValue(property);
                    return (
                      <tr key={property.key} className="odd:bg-white even:bg-emerald-50/60">
                        <td className="px-3 py-2 font-medium text-emerald-900">{label}</td>
                        <td className="px-3 py-2 text-emerald-900">{value}</td>
                        <td className="px-3 py-2 text-right">
                          {property.raw ? (
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => handleOpenRaw(property)}
                              className="h-7 px-2 text-emerald-700 hover:text-emerald-900"
                            >
                              <Code className="h-4 w-4" aria-hidden="true" />
                              <span className="sr-only">Show raw JSON</span>
                            </Button>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </DialogContent>
        </Dialog>
      )}

      <Dialog open={rawState.open} onOpenChange={(open) => { if (!open) handleCloseRaw(); }}>
        {rawState.property && (
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle>{getDisplayLabel(rawState.property, declaredUnitLabel, extractedDeclaredUnit)}</DialogTitle>
              <DialogDescription>Raw OEKOBAUDAT data snippet for this property.</DialogDescription>
            </DialogHeader>
            <pre className="max-h-[24rem] overflow-auto rounded-md bg-slate-900 px-4 py-3 text-xs text-emerald-50">
              {JSON.stringify(rawState.property.raw, null, 2)}
            </pre>
            <div className="flex justify-end">
              <Button type="button" variant="outline" onClick={handleCloseRaw}>
                Close
              </Button>
            </div>
          </DialogContent>
        )}
      </Dialog>
    </>
  );
};

export default MaterialProperties;

/*
Mock JSON fixtures for quick manual testing:
- Case A: flowProperties: [{ flowProperty: [{ property: 'bulkDensity', value: 1900, unit: 'kg/m3' }, { property: 'conversionFactor', value: 45, unit: 'kg' }] }]
  -> Inline chips show bulk density; conversion factor appears in Additional info.
- Case B: properties: [{ name: 'Layer thickness', value: 0.2, unit: 'm' }, { name: 'Surface weight', value: 12.5, unit: 'kg/m2' }]
  -> Chips display thickness and grammage, other metadata stays in the dialog.
- Case C: flowProperties: [{ flowProperty: [{ property: 'biogenicCarbonContent', value: -1000, unit: 'kg' }] }]
  -> Dialog lists "Biogenic carbon content: ND".
*/
